import React, { useEffect, useState } from "react";
import { Box, Card, Grid, Typography, useTheme, useMediaQuery, LinearProgress, Paper } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { SidebarContainer } from "../../../component/organism";
import HeadingComponent from "../../../component/atom/Headings/Heading";
import { QuizAttemptCard } from "../../../component/molecule";
import { CircularWithValueLabel } from "../../../component/atom";
import { connect } from "react-redux";
import { Actions } from "../../../core/modules/Actions";
import { IconButtonComponent } from "../../../component/atom";
import moment from 'moment';
import _ from "lodash";
import { USER_ROLE } from "../../../core/Constant";

// Get ordinal suffix for numbers (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = (num) => {
  if (!num) return '';
  
  // Handle special cases for 11, 12, 13
  if (num % 100 >= 11 && num % 100 <= 13) {
    return 'th';
  }
  
  switch (num % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const ProgressBar = ({ value, total, lowestRank, position, label }) => {
  // Calculate progress as a percentage of how close to 1st rank the student is
  // For rank 1, progress should be 100%
  // For the lowest rank, progress should be 0%
  let progress = 0;
  
  // Use lowestRank if available, otherwise fallback to total
  const maxRank = lowestRank || total;
  
  if (maxRank > 1) {
    // If student has the worst rank (equal to lowest rank), progress should be 0
    if (value === maxRank) {
      progress = 0;
    } else {
      progress = ((maxRank - value) / (maxRank - 1)) * 100;
    }
  } else {
    progress = 100; // If there's only one student, show full progress
  }
  const theme = useTheme();
  
  return (
    <Box sx={{ width: '100%', position: 'relative', my: 2 }}>
      <LinearProgress 
        variant="determinate" 
        value={progress} 
        sx={{ 
          height: 20, 
          borderRadius: 5,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 5,
          }
        }}
      />
      {/* Red dot indicator for current rank position */}
      <Box sx={{ 
        position: 'absolute', 
        left: `${position}%`, 
        top: '50%', 
        transform: 'translate(-50%, -50%)',
        width: 12, 
        height: 12, 
        borderRadius: '50%',
        backgroundColor: '#ff0000',
        border: '2px solid #fff',
        zIndex: 2,
        boxShadow: '0 0 4px rgba(0,0,0,0.3)'
      }} />
      <Box sx={{ 
        position: 'absolute', 
        left: `${position}%`, 
        top: -20, 
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <Typography variant="caption" fontWeight="bold">{label}</Typography>
        <Box sx={{ 
          width: 0, 
          height: 0, 
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: `5px solid ${theme.palette.secondary.main}`
        }} />
      </Box>
    </Box>
  );
};

const ClusterProgressBar = ({ clusterName, position, category }) => {
  const theme = useTheme();
  
  let progress = 0;
  const lowestRank = category?.lowest_rank || category?.total_students;
  
  if (category && lowestRank > 1) {
    if (category.rank === lowestRank) {
      progress = 0;
    } else {
      progress = ((lowestRank - category.rank) / (lowestRank - 1)) * 100;
    }
  } else {
    progress = 100;
  }
  
  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">{clusterName}</Typography>
        {category && (
          <Typography variant="body2">
            Your marks: <strong>{category.student_marks}</strong> | 
            Rank: <strong>{category.rank}{category.rank === 1 ? 'st' : category.rank === 2 ? 'nd' : category.rank === 3 ? 'rd' : 'th'}</strong> of {category.total_students}
          </Typography>
        )}
      </Box>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 20, 
            borderRadius: 5,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: theme.palette.primary.main,
              borderRadius: 5,
            }
          }}
        />
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            left: 10, 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#fff',
            fontWeight: 'bold'
          }}
        >
          {category ? `${category.lowest_rank || category.total_students}${getOrdinalSuffix(category.lowest_rank || category.total_students)}` : 'Last'}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            left: `${position}%`, 
            top: '50%', 
            transform: 'translateX(-50%) translateY(-50%)',
            color: theme.palette.secondary.main,
            fontWeight: 'bold',
            backgroundColor: 'rgba(255,255,255,0.7)',
            padding: '0 4px',
            borderRadius: 1
          }}
        >
          {category ? `${category.rank}${category.rank === 1 ? 'st' : category.rank === 2 ? 'nd' : category.rank === 3 ? 'rd' : 'th'}` : '2nd'}
        </Typography>
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute', 
            right: 10, 
            top: '50%', 
            transform: 'translateY(-50%)',
            color: '#fff',
            fontWeight: 'bold'
          }}
        >
          1st
        </Typography>
        <Box sx={{ 
          position: 'absolute', 
          left: `${position}%`, 
          top: '50%', 
          transform: 'translateY(-50%)',
          width: 12, 
          height: 12, 
          borderRadius: '50%',
          backgroundColor: '#ff0000',
          border: '2px solid #fff',
          zIndex: 2,
          boxShadow: '0 0 4px rgba(0,0,0,0.3)'
        }} />
      </Box>
      {category && (
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="caption" color="text.secondary">Lowest: {category.lowest_marks}</Typography>
          <Typography variant="caption" color="text.secondary">Highest: {category.highest_marks}</Typography>
        </Box>
      )}
    </Box>
  );
};

const QuizProgressRank = ({ getQuizRankData, rankData, stdgetquizAttempts, attemptsData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userType, setUserType] = useState(1);
  const [loading, setLoading] = useState(true);
  const [quizInfo, setQuizInfo] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rankPosition, setRankPosition] = useState(50); // Default position

  useEffect(() => {
    getUserType();
  }, []);

  const getUserType = async () => {
    const type = localStorage.getItem('userType');
    setUserType(type);
  };

  useEffect(() => {
    const qId = location.state?.quiz;
    if (qId?.id) {
      setQuizInfo(qId);
      getQuizRankData(qId.id);
      stdgetquizAttempts(qId.id);
    }
  }, [location, getQuizRankData, stdgetquizAttempts]);

  useEffect(() => {
    if (rankData) {
      setLoading(false);
      
      // Calculate position for the main progress bar
      // Use lowest_rank if available, otherwise fallback to total_students
      const lowestRank = rankData.lowest_rank || rankData.total_students;
      
      if (lowestRank > 1) {
        // For rank 1, position should be 100%
        // For the lowest rank, position should be 0%
        // This ensures the caret is positioned correctly based on rank
        if (rankData.rank === 1) {
          setRankPosition(100);
        } else if (rankData.rank === lowestRank) {
          setRankPosition(0);
        } else {
          // For ranks in between, distribute evenly
          const position = ((lowestRank - rankData.rank) / (lowestRank - 1)) * 100;
          setRankPosition(position);
        }
      } else {
        setRankPosition(50); // Default to middle if there's only one student
      }
    }
  }, [rankData]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularWithValueLabel value={30} />
      </Box>
    );
  }

  // Function is now defined at the top of the file
  
  const calculateClassAverage = () => {
    if (!rankData || !rankData.highest_marks || !rankData.lowest_marks || rankData.total_students < 2) {
      return null;
    }
    
    return Math.round((rankData.highest_marks + rankData.lowest_marks) / 2);
  };
  
  const classAverage = calculateClassAverage();

  return (
    <Box className="main-screen-container" sx={{ p: 2 }}>
      <Box mb={2}>
        <IconButtonComponent
          onclick={() => navigate(userType == USER_ROLE.admin ? -1 : '/quiz')}
          btnType="backbtn"
          btnText="Back"
        />
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'row', width: 1, justifyContent: 'space-between', mt: 1, mb: 3 }}>
        <Box>
          <Typography className="quiz-attempt-text" variant="h5" fontWeight="bold">
            {quizInfo?.id < 10 ? 'Quiz 0' + quizInfo?.id : 'Quiz ' + quizInfo?.id}
          </Typography>
          <Typography className="quiz-attempt-subtext" variant="subtitle1">
            {quizInfo?.name}
          </Typography>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Quiz Progress in Graph
        </Typography>
        
        <Box mt={4}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Performance overview
          </Typography>
          
          <ProgressBar 
            value={rankData?.rank || 1} 
            total={rankData?.total_students || 1}
            lowestRank={rankData?.lowest_rank} 
            position={rankPosition}
            label={`${rankData?.rank || 1}${getOrdinalSuffix(rankData?.rank || 1)}`}
          />
          
          <Box mt={3} display="flex" justifyContent="space-between">
            <Typography variant="body1" fontWeight="bold">
              {rankData?.lowest_rank ? `${rankData.lowest_rank}${getOrdinalSuffix(rankData.lowest_rank)}` : `${rankData?.total_students ? rankData.total_students : 5}${getOrdinalSuffix(rankData?.total_students || 5)}`}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              1st
            </Typography>
          </Box>
          
          <Box mt={4} display="flex" justifyContent="space-between" flexWrap="wrap">
            <Typography variant="h6" fontWeight="bold" sx={{ mb: { xs: 2, md: 0 } }}>
              Your rank: {rankData?.rank || '-'}{getOrdinalSuffix(rankData?.rank || 0)} out of {rankData?.total_students || '-'} students
            </Typography>
            <Typography variant="h6" fontWeight="bold">
              Your marks: {rankData?.student_marks || '-'}
            </Typography>
          </Box>
          
          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Lowest Marks</Typography>
                  <Typography variant="h4">{rankData?.lowest_marks || '-'}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.info.light, color: theme.palette.info.contrastText }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Your Marks</Typography>
                  <Typography variant="h4">{rankData?.student_marks || '-'}</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card sx={{ p: 2, textAlign: 'center', bgcolor: theme.palette.success.light, color: theme.palette.success.contrastText }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Highest Marks</Typography>
                  <Typography variant="h4">{rankData?.highest_marks || '-'}</Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>
          
          {rankData && rankData.category_results && rankData.category_results.length > 0 && (
            <Box mt={6} sx={{ width: '80%', mb: 2, p: 1, borderRadius: 1}}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Cluster performance
              </Typography>
              
              {rankData.category_results.map((category, index) => {
                let categoryPosition = 50;
                const lowestRank = category.lowest_rank || category.total_students;
                
                if (lowestRank > 1) {
                  if (category.rank === 1) {
                    categoryPosition = 100;
                  } else if (category.rank === lowestRank) {
                    categoryPosition = 0;
                  } else {
                    categoryPosition = ((lowestRank - category.rank) / (lowestRank - 1)) * 100;
                  }
                }
                
                return (
                  <Box key={`cluster-${index}`} sx={{ width: '100%', mb: 2, p: 1, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.02)' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Typography variant="h5" sx={{ fontSize: '0.8rem' }} fontWeight="bold">{category.category_name || `Cluster ${index + 1}`}</Typography>
                      <Typography variant="h5" sx={{ fontSize: '0.8rem' }}>
                        Rank: <strong>{category.rank}{getOrdinalSuffix(category.rank)}</strong> of {category.total_students}
                      </Typography>
                    </Box>
                    

                    <Box sx={{ position: 'relative', width: '100%', mb: 0.5 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={categoryPosition} 
                        sx={{ 
                          height: 10, 
                          borderRadius: 5,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: theme.palette.success.main,
                            borderRadius: 5,
                          }
                        }}
                      />
                      
                      {/* Red dot indicator for current rank position */}
                      <Box sx={{ 
                        position: 'absolute', 
                        left: `${categoryPosition}%`, 
                        top: '50%', 
                        transform: 'translate(-50%, -50%)',
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%',
                        backgroundColor: '#ff0000',
                        border: '1px solid #fff',
                        zIndex: 2,
                        boxShadow: '0 0 2px rgba(0,0,0,0.3)'
                      }} />
                    </Box>
                    
                    {/* Single line marks display */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="error.main" fontWeight="normal">
                        Lowest: {category.lowest_marks}
                      </Typography>
                      <Typography variant="caption" color="primary.main" fontWeight="normal">
                        Your marks: {category.student_marks}
                      </Typography>
                      <Typography variant="caption" color="success.main" fontWeight="normal">
                        Highest: {category.highest_marks}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default connect(
  state => ({
    rankData: state.quizes.get('rankData'),
    attemptsData: state.quizes.get('attemptsData')
  }),
  {
    getQuizRankData: Actions.quizes.getQuizRankData,
    stdgetquizAttempts: Actions.quizes.stdgetquizAttempts
  }
)(QuizProgressRank);
